import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Sparkles } from "lucide-react";

/**
 * Clone Company (Iter-193) — copy this company's SETTINGS into a fresh one.
 * NEVER copies transactions (vouchers/parties/items/attachments/bank
 * statement entries). The clone starts empty on financial data and inherits
 * whatever the owner checked from the 5 setting buckets.
 */
export default function CloneCompanySection({ org, currentRole, switchOrg }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    new_name: "", new_gstin: "", new_state: "",
    include: { coa: true, branches: true, users: true, bank: true, branding: true },
  });

  if (currentRole !== "business_owner") return null;

  const submit = async () => {
    if (!form.new_name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const r = await api.post("/organizations/clone", form);
      toast.success(`Created "${r.data.name}" · settings cloned`);
      setOpen(false);
      // Auto-switch to the clone so the user lands inside it.
      if (switchOrg) {
        try { await switchOrg(r.data.new_org_id); } catch { /* fall back to reload */ }
      }
      // localStorage cleared by switchOrg — reload picks up new org.
      setTimeout(() => { window.location.href = "/app/settings"; }, 700);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-t pt-4 mt-6" data-testid="clone-company-zone">
      <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 flex items-center gap-1.5">
        <Copy className="w-3.5 h-3.5" /> Multi-company shortcut
      </div>
      <div className="mt-3 flex items-start justify-between gap-4 flex-wrap rounded-sm border border-emerald-200 bg-emerald-50/40 p-4">
        <div className="flex-1 min-w-[240px]">
          <div className="text-sm font-medium text-foreground">Clone this company's settings</div>
          <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Spin up a new company that reuses <b>{org?.name || "this"}</b>'s Chart of
            Accounts, branches, users, bank setup and branding. No vouchers,
            parties or inventory are copied — the new company starts with zero
            transactional data.
          </div>
        </div>
        <Button variant="outline"
                  onClick={() => { setOpen(true); }}
                  className="rounded-sm gap-1.5 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                  data-testid="clone-company-btn">
          <Copy className="w-3.5 h-3.5" /> Clone company
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="clone-company-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Sparkles className="w-4 h-4" /> Clone "{org?.name}"
            </DialogTitle>
            <DialogDescription>
              Only settings are copied. Transactional data (vouchers, parties, items,
              statements) stays with the source company.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">New company name *</Label>
              <Input value={form.new_name} onChange={(e) => setForm({ ...form, new_name: e.target.value })}
                      className="rounded-sm mt-1" placeholder="e.g. Acme Motors (Chennai)"
                      data-testid="clone-new-name" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">New GSTIN (optional)</Label>
                <Input value={form.new_gstin} onChange={(e) => setForm({ ...form, new_gstin: e.target.value.toUpperCase() })}
                        className="rounded-sm mt-1 font-mono" data-testid="clone-new-gstin" />
              </div>
              <div>
                <Label className="text-xs">State (optional)</Label>
                <Input value={form.new_state} onChange={(e) => setForm({ ...form, new_state: e.target.value })}
                        className="rounded-sm mt-1" data-testid="clone-new-state" />
              </div>
            </div>
            <div className="pt-2">
              <Label className="text-xs">Copy these buckets</Label>
              <div className="space-y-1.5 mt-1">
                {[
                  ["coa", "Chart of Accounts + Dimensions + Segments + Doc Types + Numbering"],
                  ["branches", "Branches (GSTINs cleared) + Company profile"],
                  ["users", "Users + Roles + Permissions + Approval rules"],
                  ["bank", "Bank Accounts (zeroed) + Bank Rules + Bank Memory"],
                  ["branding", "PDF templates + Statement Format + Brand logo & accent"],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-xs" data-testid={`clone-inc-${k}`}>
                    <Checkbox checked={!!form.include[k]}
                                onCheckedChange={(v) => setForm({
                                  ...form, include: { ...form.include, [k]: !!v }
                                })} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" disabled={busy}
                      onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit}
                      disabled={busy || !form.new_name.trim()}
                      className="rounded-sm bg-gradient-to-r from-emerald-600 to-teal-600 text-white gap-1.5"
                      data-testid="clone-company-submit">
              <Copy className="w-3.5 h-3.5" /> {busy ? "Cloning…" : "Clone company"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
